import * as React from 'react'

import { Box, Button, Form } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import NotFound from 'modules/core/components/NotFound'
import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import i18n from 'modules/core/lib/i18n'

import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import { trackAddIncidenceNote } from 'modules/bookkeepers/lib/amplitudeData'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import IncidenceNoteForm from 'modules/bookkeepers/lib/forms/IncidenceNote'
import { useIncidenceNotes } from 'modules/bookkeepers/store/collections/incidenceNotes'
import TabsSelector from 'modules/payroll_bundle/components/TabsSelector'
import {
  hasInHousePayroll,
  useInhousePayroll,
} from 'modules/payroll_bundle/hooks/useInhousePayroll'

import IncidenceNoteList from './_IncidenceNoteList'

const Container = observer(() => {
  const editorRef = React.useRef<EditorHandle>(null)
  const form = React.useRef(new IncidenceNoteForm({})).current
  const contentField = form.get('content')
  const isBookkeeperView = useBookkeeperView()
  const incidence = useCurrentIncidence()
  const incidenceId = incidence?.id
  const incidenceNotes = useIncidenceNotes()
  const { hasInhousePayroll } = useInhousePayroll()

  const { running: submitting, performAction: onSubmit } = useAsyncAction(
    async () => {
      const formData = {
        content: form.data().content,
        incidence_id: incidenceId,
        bookkeeper_view: isBookkeeperView,
        inhouse_note: hasInhousePayroll,
      }

      const result = await incidenceNotes.create(formData)
      trackNewNote()

      if (result) {
        editorRef.current?.clear()
      }
    },
    {
      successFlash: true,
      errorFlash: true,
    }
  )

  const trackNewNote = () => {
    trackAddIncidenceNote(incidence, isBookkeeperView, hasInhousePayroll)
  }

  return (
    <>
      <TabsSelector selectedTab="notes" incidenceId={incidenceId} isBookkeeper={isBookkeeperView} />
      <Box overflowY="hidden">
        <Box padding="s24" overflowY="auto" gap="s24">
          <IncidenceNoteList incidenceNotesCollection={incidenceNotes} />
        </Box>
        <Form onSubmit={onSubmit} padding="s24">
          <TextEditor
            ref={editorRef}
            onChange={(value) => contentField.set(value)}
            placeholder={i18n.t('common.start_typing')}
            initialEditorState={{ data: '' }}
          />
          <Form.Buttons>
            <Button disabled={submitting || !contentField.value} submit>
              {i18n.t('bookkeepers.incidence.note.add_note')}
            </Button>
          </Form.Buttons>
        </Form>
      </Box>
    </>
  )
})

const IncidenceNoteTab = () => {
  if (hasInHousePayroll()) {
    return <Container />
  }

  return <NotFound />
}

export default IncidenceNoteTab
